import type { CollectionCodeDataItem, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { CollectionWriteDataItemOptions } from './types';
import { isNullishOrEmpty } from '#layers/base/public';
import { getCollectionFields, getCollectionPrimaryKeyFields, isPrimitiveFieldReadOnly } from '#layers/data-model/public';
import { getDefaultPrimitiveFieldWriteValue } from './defaults';

/**
 * Builds a sample write payload from writable scalar fields and optional current item values.
 */
export function getCollectionWriteDataItem(
	collection: ResolvedMonospaceCollection,
	item?: CollectionCodeDataItem,
	options: CollectionWriteDataItemOptions = {},
): CollectionCodeDataItem {
	const fields = getCollectionWritableScalarFields(collection, options);
	const data = Object.fromEntries(fields.map((field) => [field.apiName, getDefaultPrimitiveFieldWriteValue(field)])) as CollectionCodeDataItem;

	if (!item) {
		return data;
	}

	const fieldNames = new Set(fields.map((field) => field.apiName));

	// Form state can contain read-only, relation, or unknown values; only carry over writable scalar fields.
	for (const fieldName of fieldNames) {
		if (Object.hasOwn(item, fieldName)) {
			data[fieldName] = item[fieldName];
		}
	}

	return data;
}

/**
 * Returns scalar fields that can be sent in create and update payloads.
 */
function getCollectionWritableScalarFields(collection: ResolvedMonospaceCollection, options: CollectionWriteDataItemOptions = {}) {
	const primaryKeyFieldIds = new Set(getCollectionPrimaryKeyFields(collection).map((field) => field.id));
	const includePrimaryKeyFields = options.includePrimaryKeyFields === true;

	return getCollectionFields(collection, {
		includeRelationFields: false,
		includeUnsupportedFields: false,
		includeConstrainedFields: includePrimaryKeyFields,
	}).filter((field) => {
		const isPrimaryKey = primaryKeyFieldIds.has(field.id);

		// Composite primary keys are constrained but still writable on create; other constrained fields stay omitted.
		return (includePrimaryKeyFields || !isPrimaryKey)
			&& (isPrimaryKey || isNullishOrEmpty(field.constrainedBy))
			&& !isPrimitiveFieldReadOnly(field);
	});
}
